import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { studyGroupService } from '../../services/study-groups';
import type { StudyGroup, StudySession } from '../../lib/db';
import {
  Card,
  Button,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
  TextField,
  TextArea,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from '../ui';

export function StudyGroupsView() {
  const { user } = useAuth();
  const [myGroups, setMyGroups] = useState<StudyGroup[]>([]);
  const [upcomingSessions, setUpcomingSessions] = useState<StudySession[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    isPublic: true
  });

  useEffect(() => {
    if (user) {
      loadUserGroups();
      loadUpcomingSessions();
    }
  }, [user]);

  async function loadUserGroups() {
    if (!user) return;
    const groups = await studyGroupService.getUserGroups(user.id);
    setMyGroups(groups);
  }

  async function loadUpcomingSessions() {
    if (!user) return;
    const sessions = await studyGroupService.getUpcomingSessions(user.id);
    setUpcomingSessions(sessions);
  }

  async function handleCreateGroup(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;

    try {
      await studyGroupService.createGroup(
        newGroup.name,
        newGroup.description,
        user.id,
        newGroup.isPublic
      );
      setCreateDialogOpen(false);
      setNewGroup({ name: '', description: '', isPublic: true });
      loadUserGroups();
    } catch (error) {
      console.error('Failed to create group:', error);
      // TODO: Show error toast
    }
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Study Groups</h1>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>Create New Group</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogTitle>Create Study Group</DialogTitle>
            <DialogDescription>
              Create a new study group to collaborate with others.
            </DialogDescription>
            <form onSubmit={handleCreateGroup} className="space-y-4">
              <TextField
                label="Group Name"
                value={newGroup.name}
                onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                required
              />
              <TextArea
                label="Description"
                value={newGroup.description}
                onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                required
              />
              <div className="flex items-center space-x-2">
                <Switch
                  checked={newGroup.isPublic}
                  onCheckedChange={(checked) => setNewGroup({ ...newGroup, isPublic: checked })}
                />
                <span>Public Group</span>
              </div>
              <Button type="submit">Create Group</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="my-groups">
        <TabsList>
          <TabsTrigger value="my-groups">My Groups</TabsTrigger>
          <TabsTrigger value="upcoming">Upcoming Sessions</TabsTrigger>
        </TabsList>

        <TabsContent value="my-groups">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {myGroups.map((group) => (
              <Card key={group.id} className="p-4">
                <h3 className="text-lg font-semibold">{group.name}</h3>
                <p className="text-gray-600 mt-2">{group.description}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    {group.members.length} members
                  </span>
                  <Button variant="outline">View Group</Button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="upcoming">
          <div className="space-y-4">
            {upcomingSessions.map((session) => (
              <Card key={session.id} className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold">{session.name}</h3>
                    <p className="text-gray-600 mt-1">{session.description}</p>
                  </div>
                  <Button variant="outline">Join Session</Button>
                </div>
                <div className="mt-4 flex items-center space-x-4 text-sm text-gray-500">
                  <span>
                    {new Date(session.scheduledFor).toLocaleDateString()} at{' '}
                    {new Date(session.scheduledFor).toLocaleTimeString()}
                  </span>
                  <span>{session.duration} minutes</span>
                  <span>{session.participants.length} participants</span>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
